import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Icon } from '../../utils/icons';
import { useNavigate } from 'react-router-dom';
import ImageCropper from '../ImageCropper';

// Import appointment widget components
import AppointmentServices from './AppointmentServices';
import AppointmentHours from './AppointmentHours';
import AppointmentBusinessProfile from './AppointmentBusinessProfile';
import AppointmentDesign from './AppointmentDesign';
import AppointmentNotifications from './AppointmentNotifications';
import AppointmentIntegrations from './AppointmentIntegrations';
import AppointmentSettings from './AppointmentSettings';

const AppointmentWidgetConfig = forwardRef(({
    widgetConfig,
    onWidgetConfigChange,
    onCreateWidget,
    isLoading,
    onClose,
    onBack: _onBack
}, ref) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('template');
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [cropperConfig, setCropperConfig] = useState({ type: '', title: '', description: '' });

    // Validation state
    const [validationMessage, setValidationMessage] = useState('');
    const [showValidationAlert, setShowValidationAlert] = useState(false);

    // Extract accent color from style configuration for global use
    const accentColor = widgetConfig?.style?.accentColor || '#4A90E2';

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        handleValidationRedirect: (targetTab, message) => {
            setActiveTab(targetTab);
            setValidationMessage(message);
            setShowValidationAlert(true);

            // Auto hide the alert after 8 seconds
            setTimeout(() => {
                setShowValidationAlert(false);
            }, 8000);
        }
    }));

    // Check for OAuth callback parameters and switch to integrations tab if needed
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const hasOAuthCallback = urlParams.get('integration_success') || urlParams.get('integration_error');

        if (hasOAuthCallback) {
            setActiveTab('integrations');
        }
    }, []);

    // Custom back function for Appointment widget - go to dashboard instead of Step 1
    const handleBack = () => {
        navigate('/dashboard');
    };

    // Helper function to check if tab requires attention based on configuration
    const getTabValidationStatus = (tabId) => {
        const config = widgetConfig.appointmentConfig || {};

        switch (tabId) {
            case 'services': {
                const services = config.services || [];
                if (services.length === 0) {
                    return { needsAttention: false };
                }
                const invalidServices = services.filter(
                    service => !service.name?.trim() || !service.duration
                );
                if (invalidServices.length > 0) {
                    return { needsAttention: true, reason: 'Services missing details' };
                }
                return { needsAttention: false };
            }

            case 'hours': {
                const availableHours = config.availableHours || {};
                const hasAvailableDay = Object.values(availableHours).some(day => day.available);
                if (!hasAvailableDay) {
                    return { needsAttention: true, reason: 'No available days set' };
                }
                return { needsAttention: false };
            }

            case 'design':
                if (!config.widgetTitle?.trim()) {
                    return { needsAttention: true, reason: 'Widget title missing' };
                }
                return { needsAttention: false };

            case 'notifications':
                if (config.notifications?.ownerNotification && !config.notifications?.ownerEmail?.trim()) {
                    return { needsAttention: true, reason: 'Owner email required' };
                }
                return { needsAttention: false };

            default:
                return { needsAttention: false };
        }
    };

    const tabs = [
        { id: 'template', name: 'Template', icon: 'grid' },
        { id: 'services', name: 'Services', icon: 'business' },
        { id: 'hours', name: 'Hours', icon: 'schedule' },
        { id: 'profile', name: 'Business Profile', icon: 'work' },
        { id: 'design', name: 'Design', icon: 'palette' },
        { id: 'notifications', name: 'Notifications', icon: 'email' },
        { id: 'integrations', name: 'Integrations', icon: 'link' },
        { id: 'settings', name: 'Settings', icon: 'settings' }
    ];

    // Update widget configuration
    const updateConfig = (updates) => {
        onWidgetConfigChange({
            ...widgetConfig,
            ...updates
        });
    };

    // Template selection options
    const templateOptions = [
        {
            id: 'inline-form',
            name: 'Inline Booking Form',
            description: 'Display the booking form directly on your webpage',
            icon: 'article',
            preview: '/appointment_preview_inline_form.png'
        },
        {
            id: 'inline-button',
            name: 'Inline Booking Button',
            description: 'Show a button that opens the booking form when clicked',
            icon: 'web',
            preview: '/appointment_preview_inline_button.png'
        }
    ];

    // Appointment Widget Preview Component
    const AppointmentWidgetPreview = React.memo(() => {
        const config = widgetConfig.appointmentConfig || {};
        const style = widgetConfig.style || {};
        const businessProfile = config.businessProfile || {};

        const templateType = config.templateType || 'inline-form';
        const widgetTitle = config.widgetTitle || 'Book Your Appointment';
        const backgroundColor = style.backgroundColor || '#FFFFFF';
        const textColor = style.textColor || '#000000';
        const fontFamily = style.fontFamily || 'default';
        const services = config.services || [];
        const isCalendarMode = config.dateDisplayMode === 'calendar';

        // State for selected time and form display
        const [selectedTime, setSelectedTime] = React.useState(null);
        const [showBookingForm, setShowBookingForm] = React.useState(false);
        const [selectedDate, setSelectedDate] = React.useState(0); // Today is index 0
        const [showNotes, setShowNotes] = React.useState(false);
        // Calendar mode: stepped flow (1 = date, 2 = time, 3 = details) + displayed month
        const [calStep, setCalStep] = React.useState(1);
        const [calViewDate, setCalViewDate] = React.useState(new Date());
        const [showModal, setShowModal] = React.useState(false);
        const [selectedServiceId, setSelectedServiceId] = React.useState(null);
        const [expandedDescriptions, setExpandedDescriptions] = React.useState({});

        // State for service display views
        // currentSlideIndex state removed - using list view only
        const [visibleServiceCount, setVisibleServiceCount] = React.useState(5);

        // Helper function to generate dynamic focus styles
        const getInputFocusStyle = () => ({
            borderColor: accentColor,
            boxShadow: `0 0 0 2px ${accentColor}20`, // 20% opacity for ring effect
            outline: 'none'
        });


        // Get hours configuration
        const hoursConfig = config.availableHours || {
            monday: { available: true, start: '10:00', end: '19:00' },
            tuesday: { available: true, start: '10:00', end: '19:00' },
            wednesday: { available: true, start: '10:00', end: '19:00' },
            thursday: { available: true, start: '10:00', end: '19:00' },
            friday: { available: true, start: '10:00', end: '19:00' },
            saturday: { available: true, start: '10:00', end: '19:00' },
            sunday: { available: true, start: '10:00', end: '19:00' }
        };
        const dateSpecificHours = config.dateSpecificHours || [];
        const minAdvanceBooking = config.minAdvanceBooking || { value: 1, unit: 'hour' };
        const maxAdvanceBooking = config.maxAdvanceBooking || { value: 2, unit: 'months' };

        // Get current date info
        const currentDate = new Date();
        const today = currentDate.getDate();
        const currentMonth = currentDate.toLocaleDateString('en-US', { month: 'long' });

        // Helper function to convert advance booking to milliseconds
        const getAdvanceBookingMs = (advanceBooking) => {
            const { value, unit } = advanceBooking;
            const msPerUnit = {
                'minutes': 60 * 1000,
                'hour': 60 * 60 * 1000,
                'hours': 60 * 60 * 1000,
                'days': 24 * 60 * 60 * 1000,
                'weeks': 7 * 24 * 60 * 60 * 1000,
                'month': 30 * 24 * 60 * 60 * 1000,
                'months': 30 * 24 * 60 * 60 * 1000
            };
            return value * (msPerUnit[unit] || msPerUnit.hours);
        };

        // Calculate date range for booking
        const minAdvanceMs = getAdvanceBookingMs(minAdvanceBooking);
        const maxAdvanceMs = getAdvanceBookingMs(maxAdvanceBooking);
        const earliestBookingDate = new Date(currentDate.getTime() + minAdvanceMs);
        const latestBookingDate = new Date(currentDate.getTime() + maxAdvanceMs);

        // Generate date options based on available days and advance booking settings
        const dateOptions = Array.from({ length: 14 }, (_, i) => {
            const date = new Date(currentDate);
            date.setDate(today + i);

            // Get day of week (lowercase)
            const dayKey = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

            // Check if this day is available based on hours configuration
            const isWithinBookingWindow = date >= earliestBookingDate && date <= latestBookingDate;
            const isDayAvailable = hoursConfig[dayKey]?.available || false;

            // Check date-specific hours
            const dateSpecific = dateSpecificHours.find(item => {
                const startDate = new Date(item.startDate);
                const endDate = new Date(item.endDate);
                return date >= startDate && date <= endDate;
            });

            let isAvailable = isDayAvailable && isWithinBookingWindow;
            let hours = hoursConfig[dayKey];

            if (dateSpecific) {
                isAvailable = dateSpecific.available && isWithinBookingWindow;
                if (dateSpecific.available) {
                    hours = dateSpecific.hours;
                }
            }

            return {
                day: date.toLocaleDateString('en-US', { weekday: 'short' }),
                date: date.getDate(),
                fullDate: date.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' }),
                full: date,
                isAvailable,
                hours: hours || { start: '10:00', end: '19:00' },
                dayKey
            };
        }).filter(dateOption => dateOption.isAvailable).slice(0, 7); // Show first 7 available dates

        // Generate time slots based on selected date's hours
        const generateTimeSlots = (selectedDateOption) => {
            if (!selectedDateOption || !selectedDateOption.hours) {
                return { morningSlots: [], afternoonSlots: [], eveningSlots: [] };
            }

            const { start, end } = selectedDateOption.hours;
            const startTime = new Date(`2000-01-01T${start}:00`);
            const endTime = new Date(`2000-01-01T${end}:00`);

            const slots = [];
            const current = new Date(startTime);

            // Generate 30-minute slots
            while (current < endTime) {
                const timeString = current.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
                slots.push(timeString);
                current.setMinutes(current.getMinutes() + 30);
            }

            // Categorize slots
            const morningSlots = slots.filter(slot => {
                const hour = parseInt(slot.split(':')[0]);
                const isPM = slot.includes('PM');
                // Morning: 6:00 AM - 10:59 AM
                return !isPM && hour >= 6 && hour < 11;
            });

            const afternoonSlots = slots.filter(slot => {
                const hour = parseInt(slot.split(':')[0]);
                const isPM = slot.includes('PM');
                // Afternoon: 11:00 AM - 5:59 PM (includes 12:xx PM as hour 12)
                return (!isPM && hour >= 11) || (isPM && (hour === 12 || (hour >= 1 && hour < 6)));
            });

            const eveningSlots = slots.filter(slot => {
                const hour = parseInt(slot.split(':')[0]);
                const isPM = slot.includes('PM');
                // Evening: 6:00 PM and later
                return isPM && hour >= 6 && hour !== 12; // Exclude 12 PM (noon) from evening
            });

            return { morningSlots, afternoonSlots, eveningSlots };
        };

        // Get time slots for currently selected date
        const selectedDateOption = dateOptions[selectedDate] || null;
        const { morningSlots, afternoonSlots, eveningSlots } = generateTimeSlots(selectedDateOption);

        // Check if business is currently open
        const getCurrentOpenStatus = () => {
            const now = new Date();
            const currentDayKey = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
            const currentTime = now.toTimeString().slice(0, 5); // HH:MM format

            // Check date-specific hours first
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const dateSpecific = dateSpecificHours.find(item => {
                const startDate = new Date(item.startDate);
                const endDate = new Date(item.endDate);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                return today >= startDate && today <= endDate;
            });

            let isOpen = false;
            let closeTime = '17:00';

            if (dateSpecific) {
                if (dateSpecific.available) {
                    isOpen = currentTime >= dateSpecific.hours.start && currentTime < dateSpecific.hours.end;
                    closeTime = dateSpecific.hours.end;
                }
            } else if (hoursConfig[currentDayKey]?.available) {
                const { start, end } = hoursConfig[currentDayKey];
                isOpen = currentTime >= start && currentTime < end;
                closeTime = end;
            }

            return { isOpen, closeTime };
        };

        const { isOpen, closeTime } = getCurrentOpenStatus();

        // Currency options for displaying correct symbols
        const currencyOptions = [
            { value: 'USD', label: 'USD ($)', symbol: '$' },
            { value: 'EUR', label: 'EUR (€)', symbol: '€' },
            { value: 'GBP', label: 'GBP (£)', symbol: '£' },
            { value: 'CAD', label: 'CAD (C$)', symbol: 'C$' },
            { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
            { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
            { value: 'CHF', label: 'CHF (₣)', symbol: '₣' },
            { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
            { value: 'INR', label: 'INR (₹)', symbol: '₹' },
            { value: 'KRW', label: 'KRW (₩)', symbol: '₩' },
            { value: 'BRL', label: 'BRL (R$)', symbol: 'R$' },
            { value: 'MXN', label: 'MXN ($)', symbol: '$' },
            { value: 'SGD', label: 'SGD (S$)', symbol: 'S$' },
            { value: 'HKD', label: 'HKD (HK$)', symbol: 'HK$' },
            { value: 'NZD', label: 'NZD (NZ$)', symbol: 'NZ$' }
        ];

        // Initialize selected service ID if not set
        React.useEffect(() => {
            if (!selectedServiceId && services.length > 0) {
                setSelectedServiceId(services[0].id);
            }
        }, [services, selectedServiceId]);

        // Get selected service or use first service or default
        const selectedService = React.useMemo(() => {
            if (services.length > 0) {
                const service = services.find(s => s.id === selectedServiceId) || services[0];
                return service;
            }
            return {
                id: 'default',
                name: "Men's Haircut",
                description: "Get a professional men's haircut tailored to your style preferences.",
                price: "50",
                currency: "USD",
                duration: "30"
            };
        }, [services, selectedServiceId]);

        // Get currency symbol for selected service
        const getCurrencySymbol = (currency) => {
            const currencyOption = currencyOptions.find(option => option.value === currency);
            return currencyOption ? currencyOption.symbol : '$';
        };

        // Handle description expansion
        const toggleDescription = (serviceId) => {
            setExpandedDescriptions(prev => ({
                ...prev,
                [serviceId]: !prev[serviceId]
            }));
        };

        // Helper function to truncate text and show read more
        const renderDescription = (service, isExpanded) => {
            if (!service.description) return null;

            const maxLength = 120; // Characters to show before truncation
            const shouldTruncate = service.description.length > maxLength;

            if (!shouldTruncate) {
                return (
                    <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                        {service.description}
                    </p>
                );
            }

            return (
                <div className="mt-2">
                    <p className="text-gray-600 text-sm leading-relaxed">
                        {isExpanded ? service.description : `${service.description.substring(0, maxLength)}...`}
                    </p>
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent service selection when clicking read more
                            toggleDescription(service.id);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-1 transition-colors"
                    >
                        {isExpanded ? 'Show less' : 'Read more'}
                    </button>
                </div>
            );
        };

        // Handle time selection
        const handleTimeSelect = (time) => {
            setSelectedTime(time);
            setShowBookingForm(true);
            if (isCalendarMode) setCalStep(3);
        };

        // Handle change date/time
        const handleChangeDateTime = () => {
            setSelectedTime(null);
            setShowBookingForm(false);
            setShowNotes(false);
            if (isCalendarMode) setCalStep(1);
        };

        // Calendar mode: pick a day (index into available dateOptions) -> go to time step
        const handleCalSelectDay = (index) => {
            setSelectedDate(index);
            setSelectedTime(null);
            setShowBookingForm(false);
            setCalStep(2);
        };

        // Calendar mode: back to date step
        const handleCalBackToDate = () => {
            setSelectedTime(null);
            setShowBookingForm(false);
            setShowNotes(false);
            setCalStep(1);
        };

        // Calendar mode: back to time step
        const handleCalBackToTime = () => {
            setSelectedTime(null);
            setShowBookingForm(false);
            setShowNotes(false);
            setCalStep(2);
        };

        // Handle add notes
        const handleAddNotes = () => {
            setShowNotes(true);
        };

        // Handle service selection
        const handleServiceSelect = (serviceId) => {
            setSelectedServiceId(serviceId);
            // Reset form state when changing service
            setSelectedTime(null);
            setShowBookingForm(false);
            setShowNotes(false);
            if (isCalendarMode) setCalStep(1);
        };

        // Handle load more services
        const handleLoadMore = () => {
            setVisibleServiceCount(prev => Math.min(prev + 5, services.length));
        };

        // Auto-slide functionality removed - using list view only

        // Reset visible count when services change
        React.useEffect(() => {
            setVisibleServiceCount(5);
        }, [services.length]);

        // Handle modal open/close for inline-button template
        const handleOpenModal = () => {
            setShowModal(true);
            setSelectedTime(null);
            setShowBookingForm(false);
            setShowNotes(false);
            if (isCalendarMode) setCalStep(1);
        };

        const handleCloseModal = () => {
            setShowModal(false);
            setSelectedTime(null);
            setShowBookingForm(false);
            setShowNotes(false);
        };

        // Render the stepped calendar flow (calendar mode only)
        const renderCalendarFlow = () => {
            const monthLabel = calViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            const year = calViewDate.getFullYear();
            const month = calViewDate.getMonth();
            const startWeekday = new Date(year, month, 1).getDay();
            const daysInMonth = new Date(year, month + 1, 0).getDate();

            const availByKey = {};
            dateOptions.forEach((opt, idx) => {
                const f = opt.full;
                availByKey[f.getFullYear() + '-' + f.getMonth() + '-' + f.getDate()] = idx;
            });

            const cells = [];
            for (let b = 0; b < startWeekday; b++) {
                cells.push(<div key={'blank-' + b} />);
            }
            for (let day = 1; day <= daysInMonth; day++) {
                const availIdx = availByKey[year + '-' + month + '-' + day];
                const selectable = availIdx !== undefined;
                const isSelected = selectable && selectedDate === availIdx;
                cells.push(
                    <button
                        key={'day-' + day}
                        type="button"
                        disabled={!selectable}
                        onClick={() => selectable && handleCalSelectDay(availIdx)}
                        className={`py-2 rounded-lg text-sm font-semibold transition-all ${selectable
                            ? (isSelected ? 'text-white shadow-md' : 'text-gray-700 bg-gray-100 hover:bg-gray-200')
                            : 'text-gray-300 cursor-not-allowed'
                            }`}
                        style={isSelected ? { backgroundColor: accentColor } : {}}
                    >
                        {day}
                    </button>
                );
            }

            const steps = [{ n: 1, label: 'Date' }, { n: 2, label: 'Time' }, { n: 3, label: 'Details' }];
            const StepIndicator = (
                <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
                    {steps.map((s, i) => {
                        const active = s.n <= calStep;
                        return (
                            <React.Fragment key={s.n}>
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                                        style={active ? { backgroundColor: accentColor, color: '#fff' } : { backgroundColor: '#E5E7EB', color: '#6B7280' }}
                                    >
                                        {s.n}
                                    </span>
                                    <span className="text-xs font-semibold" style={{ color: active ? '#111827' : '#6B7280' }}>{s.label}</span>
                                </div>
                                {i < steps.length - 1 && <span className="w-6 h-px bg-gray-200" />}
                            </React.Fragment>
                        );
                    })}
                </div>
            );

            const noSlots = morningSlots.length === 0 && afternoonSlots.length === 0 && eveningSlots.length === 0;
            const TimeGroup = (label, slots) => (
                slots.length > 0 && (
                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">{label}</h3>
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                            {slots.map((time) => (
                                <button
                                    key={time}
                                    onClick={() => handleTimeSelect(time)}
                                    className="py-3 px-2 text-sm font-medium rounded-lg text-black bg-gray-100 hover:bg-gray-200 transition-all min-w-0"
                                >
                                    {time}
                                </button>
                            ))}
                        </div>
                    </div>
                )
            );

            return (
                <div className="mb-8">
                    <div className="border-t border-gray-200 pt-6">
                        {StepIndicator}

                        {calStep === 1 && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-gray-900">{monthLabel}</h2>
                                    <div className="flex space-x-2">
                                        <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setCalViewDate(new Date(year, month - 1, 1))}>
                                            <Icon name="back" size={20} className="text-gray-600" />
                                        </button>
                                        <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setCalViewDate(new Date(year, month + 1, 1))}>
                                            <Icon name="forward" size={20} className="text-gray-600" />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                                        <div key={d} className="text-center text-xs font-semibold text-gray-400 uppercase py-1">{d}</div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                                    {cells}
                                </div>
                            </div>
                        )}

                        {calStep === 2 && (
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <button onClick={handleCalBackToDate} className="inline-flex items-center gap-1 font-semibold text-sm" style={{ color: accentColor }}>
                                        <Icon name="back" size={16} /> Back
                                    </button>
                                    <span className="text-sm font-semibold text-gray-900">{dateOptions[selectedDate]?.fullDate}</span>
                                </div>
                                {noSlots ? (
                                    <div className="text-center py-8">
                                        <div className="text-gray-400 mb-2"><Icon name="time" size={48} className="mx-auto opacity-50" /></div>
                                        <p className="text-gray-500 text-sm">No available time slots</p>
                                        <p className="text-gray-400 text-xs">Please select a different date</p>
                                    </div>
                                ) : (
                                    <>
                                        {TimeGroup('Morning', morningSlots)}
                                        {TimeGroup('Afternoon', afternoonSlots)}
                                        {TimeGroup('Evening', eveningSlots)}
                                    </>
                                )}
                            </div>
                        )}

                        {calStep === 3 && (
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{dateOptions[selectedDate]?.fullDate}</h3>
                                    <div className="flex items-center text-gray-600">
                                        <Icon name="time" size={16} className="mr-1" />
                                        <span>{selectedTime}</span>
                                    </div>
                                </div>
                                <button onClick={handleCalBackToTime} className="inline-flex items-center gap-1 font-semibold text-sm" style={{ color: accentColor }}>
                                    <Icon name="back" size={16} /> Back
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            );
        };

        // Render the booking form content (used both in inline and modal)
        const BookingFormContent = () => (
            <>
                {/* Service Selection - only shown when services are configured */}
                {services.length > 1 ? (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Service</h3>

                        {/* List View Only */}
                        <div className="space-y-3">
                            {services.slice(0, visibleServiceCount).map((service) => {
                                const isExpanded = expandedDescriptions[service.id];
                                return (
                                    <div
                                        key={service.id}
                                        onClick={() => handleServiceSelect(service.id)}
                                        className={`flex items-start space-x-4 p-5 rounded-xl border cursor-pointer transition-all ${selectedServiceId === service.id
                                            ? 'shadow-md'
                                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                            }`}
                                        style={selectedServiceId === service.id ? {
                                            borderColor: accentColor,
                                            backgroundColor: `${accentColor}0D` // 5% opacity
                                        } : {}}
                                    >
                                        {/* Image Section */}
                                        <div className="flex-shrink-0">
                                            <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden">
                                                {(service.image || service.imageUrl) ? (
                                                    <img
                                                        src={service.image || service.imageUrl}
                                                        alt={service.name}
                                                        className="w-full h-full object-cover"
                                                        onLoad={(e) => {
                                                            console.log(`✅ Preview: Service "${service.name}" image loaded:`, e.target.src);
                                                        }}
                                                        onError={(e) => {
                                                            console.error(`❌ Preview: Service "${service.name}" image failed to load:`, e.target.src);
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-black rounded-xl flex items-center justify-center">
                                                        <Icon name="cut" size={28} className="text-white" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div className="flex-1 min-w-0">
                                            {/* Header with title and selection indicator */}
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-gray-900 text-lg leading-tight">{service.name}</h4>
                                                </div>
                                                {selectedServiceId === service.id && (
                                                    <div
                                                        className="w-6 h-6 rounded-full flex items-center justify-center ml-3 flex-shrink-0"
                                                        style={{ backgroundColor: accentColor }}
                                                    >
                                                        <Icon name="check" size={14} className="text-white" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Description with Read More */}
                                            {renderDescription(service, isExpanded)}

                                            {/* Price and Duration */}
                                            <div className="flex items-center space-x-2 mt-2">
                                                {service.price && (
                                                    <>
                                                        <span className="text-gray-900 font-bold text-lg">
                                                            {getCurrencySymbol(service.currency || 'USD')}{service.price}
                                                        </span>
                                                        <span className="text-gray-400">•</span>
                                                    </>
                                                )}
                                                <div className="flex items-center space-x-1 text-gray-600">
                                                    <Icon name="time" size={16} className="text-gray-400" />
                                                    <span className="text-sm font-medium">{service.duration} min</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Load More Button */}
                        {services.length > visibleServiceCount && (
                            <div className="flex justify-center mt-4">
                                <button
                                    onClick={handleLoadMore}
                                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center space-x-2"
                                >
                                    <Icon name="add" size={16} />
                                    <span>Load More ({services.length - visibleServiceCount} remaining)</span>
                                </button>
                            </div>
                        )}
                    </div>
                ) : services.length === 1 ? (
                    /* Single Service Display */
                    <div className="mb-8">
                        <div className="flex items-start space-x-4 p-5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            {/* Image Section - Larger for single service */}
                            <div className="flex-shrink-0">
                                <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden">
                                    {(selectedService.image || selectedService.imageUrl) ? (
                                        <img
                                            src={selectedService.image || selectedService.imageUrl}
                                            alt={selectedService.name}
                                            className="w-full h-full object-cover"
                                            onLoad={(e) => {
                                                console.log(`✅ Single Service Preview: "${selectedService.name}" image loaded:`, e.target.src);
                                            }}
                                            onError={(e) => {
                                                console.error(`❌ Single Service Preview: "${selectedService.name}" image failed:`, e.target.src);
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-gray-700 to-black rounded-xl flex items-center justify-center">
                                            <Icon name="cut" size={32} className="text-white" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Content Section */}
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 text-xl leading-tight mb-2">{selectedService.name}</h3>

                                {/* Description with Read More */}
                                {renderDescription(selectedService, expandedDescriptions[selectedService.id])}

                                {/* Price and Duration */}
                                <div className="flex items-center space-x-2 mt-2">
                                    {selectedService.price && (
                                        <>
                                            <span className="text-gray-900 font-bold text-xl">
                                                {getCurrencySymbol(selectedService.currency || 'USD')}{selectedService.price}
                                            </span>
                                            <span className="text-gray-400">•</span>
                                        </>
                                    )}
                                    <div className="flex items-center space-x-1 text-gray-600">
                                        <Icon name="time" size={18} className="text-gray-400" />
                                        <span className="text-base font-medium">{selectedService.duration} minutes</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Date/Time Display - Shows selected date and time with change link */}
                {!isCalendarMode && (showBookingForm ? (
                    <div className="mb-8">
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                        {dateOptions[selectedDate]?.fullDate}
                                    </h3>
                                    <div className="flex items-center text-gray-600">
                                        <Icon name="time" size={16} className="mr-1" />
                                        <span>{selectedTime}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={handleChangeDateTime}
                                    className="text-blue-600 hover:text-blue-700 transition-colors font-medium"
                                >
                                    Change
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Date Selection */}
                        <div className="mb-8">
                            <div className="border-t border-gray-200 pt-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {currentMonth}
                                    </h2>
                                    <div className="flex space-x-2">
                                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                                            <Icon name="back" size={20} className="text-gray-600" />
                                        </button>
                                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                                            <Icon name="forward" size={20} className="text-gray-600" />
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-7 gap-2 sm:gap-4 mb-6">
                                    {dateOptions.map((dateOption, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedDate(index)}
                                            className={`py-3 sm:py-5 px-1 sm:px-3 rounded-xl text-center transition-all ${selectedDate === index
                                                ? 'text-white font-medium shadow-md'
                                                : 'text-gray-600 bg-gray-100 hover:bg-gray-200 font-medium hover:shadow-sm'
                                                }`}
                                            style={selectedDate === index ? { backgroundColor: accentColor } : {}}
                                        >
                                            <div className="text-xs sm:text-sm">{index === 0 ? 'Today' : dateOption.day}</div>
                                            <div className="text-sm sm:text-lg font-bold">{dateOption.date}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Time Selection */}
                        <div className="mb-8">
                            {morningSlots.length === 0 && afternoonSlots.length === 0 && eveningSlots.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="text-gray-400 mb-2">
                                        <Icon name="time" size={48} className="mx-auto opacity-50" />
                                    </div>
                                    <p className="text-gray-500 text-sm">No available time slots</p>
                                    <p className="text-gray-400 text-xs">Please select a different date</p>
                                </div>
                            ) : (
                                <>
                                    {/* Morning */}
                                    {morningSlots.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Morning</h3>
                                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                                                {morningSlots.map((time) => (
                                                    <button
                                                        key={time}
                                                        onClick={() => handleTimeSelect(time)}
                                                        className="py-3 px-2 text-sm font-medium rounded-lg text-black bg-gray-100 hover:bg-gray-200 transition-all min-w-0"
                                                    >
                                                        {time}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Afternoon */}
                                    {afternoonSlots.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Afternoon</h3>
                                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                                                {afternoonSlots.map((time) => (
                                                    <button
                                                        key={time}
                                                        onClick={() => handleTimeSelect(time)}
                                                        className="py-3 px-2 text-sm font-medium rounded-lg text-black bg-gray-100 hover:bg-gray-200 transition-all min-w-0"
                                                    >
                                                        {time}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Evening */}
                                    {eveningSlots.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Evening</h3>
                                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                                                {eveningSlots.map((time) => (
                                                    <button
                                                        key={time}
                                                        onClick={() => handleTimeSelect(time)}
                                                        className="py-3 px-2 text-sm font-medium rounded-lg text-black bg-gray-100 hover:bg-gray-200 transition-all min-w-0"
                                                    >
                                                        {time}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                ))}

                {/* Calendar Mode - stepped date/time flow */}
                {isCalendarMode && renderCalendarFlow()}

                {/* Booking Form - Shows when time is selected */}
                {showBookingForm && (
                    <div className="mb-8">
                        <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-6">Enter Details</h3>

                            <div className="space-y-4">
                                {/* Name Field */}
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Name"
                                        className="w-full px-4 py-3 border rounded-lg outline-none transition-colors"
                                        style={{ borderColor: accentColor }}
                                        onFocus={(e) => Object.assign(e.target.style, getInputFocusStyle())}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = accentColor;
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                {/* Phone Field - Required */}
                                <div>
                                    <input
                                        type="tel"
                                        placeholder="Phone Number *"
                                        className="w-full px-4 py-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                                    />
                                    <p className="text-red-500 text-sm mt-1">This field is required</p>
                                </div>

                                {/* Email Field */}
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-colors"
                                        onFocus={(e) => Object.assign(e.target.style, getInputFocusStyle())}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#d1d5db'; // gray-300
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                </div>

                                {/* Add Notes Link / Notes Field */}
                                {!showNotes ? (
                                    <div className="text-right">
                                        <button
                                            onClick={handleAddNotes}
                                            className="hover:opacity-80 transition-colors font-medium"
                                            style={{ color: accentColor }}
                                        >
                                            Add Notes
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Notes (Optional)
                                        </label>
                                        <textarea
                                            rows={3}
                                            placeholder="Add any special requests or notes..."
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-colors resize-none"
                                            onFocus={(e) => Object.assign(e.target.style, getInputFocusStyle())}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#d1d5db'; // gray-300
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Confirm Booking Button */}
                                <div className="pt-4">
                                    <button
                                        className="w-full py-4 rounded-lg text-white font-semibold transition-colors shadow-sm hover:shadow-md"
                                        style={{ backgroundColor: accentColor }}
                                    >
                                        Confirm Booking
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );

        // Check if business profile should be shown
        const showBusinessProfile = businessProfile.showProfile !== false; // Default to true

        // Font family mapping
        const getFontFamilyStyle = (fontFamily) => {
            const fontMap = {
                'default': 'system-ui, -apple-system, sans-serif',
                'inter': '"Inter", sans-serif',
                'roboto': '"Roboto", sans-serif',
                'open-sans': '"Open Sans", sans-serif',
                'lato': '"Lato", sans-serif',
                'montserrat': '"Montserrat", sans-serif',
                'poppins': '"Poppins", sans-serif',
                'source-sans-pro': '"Source Sans Pro", sans-serif',
                'nunito': '"Nunito", sans-serif',
                'raleway': '"Raleway", sans-serif',
                'ubuntu': '"Ubuntu", sans-serif'
            };
            return fontMap[fontFamily] || fontMap['default'];
        };

        // Business Profile Component
        const BusinessProfileSection = () => (
            <div
                className="lg:w-[400px] w-full relative rounded-3xl overflow-hidden lg:mr-6 mb-8 lg:mb-0"
                style={{
                    backgroundColor: backgroundColor === '#FFFFFF' ? '#f9fafb' : backgroundColor
                }}
            >
                {/* Cover Image */}
                {businessProfile.coverImage ? (
                    <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: `url(${businessProfile.coverImage})` }}>
                        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                    </div>
                ) : (
                    <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80')` }}>
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/70 to-blue-500/70"></div>
                    </div>
                )}

                {/* Business Logo - Overlapping */}
                <div className="absolute top-32 left-6">
                    <div className="w-24 h-24 bg-white rounded-full p-2 border-4 border-white">
                        {businessProfile.logo ? (
                            <img
                                src={businessProfile.logo}
                                alt="Business Logo"
                                className="w-full h-full object-cover rounded-full"
                            />
                        ) : (
                            <div className="w-full h-full bg-white rounded-full flex items-center justify-center relative overflow-hidden">
                                <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                                        <Icon name="work" size={20} className="text-purple-600" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Business Info */}
                <div className="pt-14 px-6 pb-6">
                    <div className="mb-5">
                        <h2 className="text-2xl font-bold mb-1" style={{ color: textColor }}>
                            {businessProfile.businessName || 'Elite Cuts Salon'}
                        </h2>
                        <h3 className="text-lg font-semibold mb-3" style={{ color: textColor, opacity: 0.8 }}>Business Profile</h3>
                        <div className="text-sm leading-relaxed mb-2" style={{ color: textColor, opacity: 0.7 }}>
                            {businessProfile.description ? (
                                <div dangerouslySetInnerHTML={{ __html: businessProfile.description }} />
                            ) : (
                                <p>Elite Cuts Salon offers premium hair services tailored to your style. Our experienced stylists provide top-notch haircuts, coloring, and styling services.</p>
                            )}
                        </div>
                        {businessProfile.description && businessProfile.description.length > 150 && (
                            <button
                                className="text-sm font-medium transition-colors"
                                style={{ color: accentColor }}
                            >
                                Read More
                            </button>
                        )}
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-3">
                        {/* Phone - Show if provided */}
                        {businessProfile.contact?.phone && (
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                                    <Icon name="phone" size={18} className="text-blue-600" />
                                </div>
                                <span className="font-medium" style={{ color: textColor, opacity: 0.8 }}>
                                    {businessProfile.contact.phone}
                                </span>
                            </div>
                        )}

                        {/* WhatsApp - Show if provided */}
                        {businessProfile.contact?.whatsapp && (
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                                    <Icon name="whatsapp" size={18} className="text-green-600" />
                                </div>
                                <span style={{ color: textColor, opacity: 0.8 }}>{businessProfile.contact.whatsapp}</span>
                            </div>
                        )}

                        {/* Instagram - Show if provided */}
                        {businessProfile.contact?.instagram && (
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-pink-50 rounded-full flex items-center justify-center">
                                    <Icon name="instagram" size={18} className="text-pink-600" />
                                </div>
                                <span style={{ color: textColor, opacity: 0.8 }}>
                                    {businessProfile.contact.instagram}
                                </span>
                            </div>
                        )}

                        {/* Address - Show if provided */}
                        {businessProfile.contact?.address && (
                            <div className="flex items-start space-x-3">
                                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                                    <Icon name="location" size={18} className="text-red-600" />
                                </div>
                                <div style={{ color: textColor, opacity: 0.8 }}>
                                    {businessProfile.contact.address}
                                </div>
                            </div>
                        )}

                        {/* Email - Show if provided */}
                        {businessProfile.contact?.email && (
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center">
                                    <Icon name="email" size={18} className="text-purple-600" />
                                </div>
                                <span style={{ color: textColor, opacity: 0.8 }}>
                                    {businessProfile.contact.email}
                                </span>
                            </div>
                        )}

                        {/* Website - Show if provided */}
                        {businessProfile.contact?.website && (
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center">
                                    <Icon name="web" size={18} className="text-indigo-600" />
                                </div>
                                <a
                                    href={businessProfile.contact.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="transition-colors"
                                    style={{ color: textColor, opacity: 0.8 }}
                                >
                                    {businessProfile.contact.website}
                                </a>
                            </div>
                        )}

                        {/* Default contact info when nothing is provided */}
                        {!businessProfile.contact?.phone &&
                            !businessProfile.contact?.whatsapp &&
                            !businessProfile.contact?.instagram &&
                            !businessProfile.contact?.address &&
                            !businessProfile.contact?.email &&
                            !businessProfile.contact?.website && (
                                <div className="text-center py-4">
                                    <div className="text-gray-400 mb-2">
                                        <Icon name="phone" size={24} className="mx-auto opacity-50" />
                                    </div>
                                    <p className="text-sm" style={{ color: textColor, opacity: 0.5 }}>Contact information not provided</p>
                                </div>
                            )}
                    </div>

                    {/* Hours - Open Status */}
                    <div className={`mt-4 p-3 border rounded-lg ${isOpen
                        ? 'bg-green-50 border-green-100'
                        : 'bg-red-50 border-red-100'
                        }`}>
                        <div className="flex items-center space-x-2">
                            <Icon name="time" size={16} className={isOpen ? "text-green-600" : "text-red-600"} />
                            <span className={`font-medium ${isOpen ? 'text-green-800' : 'text-red-800'
                                }`}>
                                {isOpen
                                    ? `Open until ${new Date(`2000-01-01T${closeTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
                                    : 'Closed now'
                                }
                            </span>
                            <button className="text-gray-400 hover:text-gray-600">
                                <Icon name="dropdown" size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );

        return (
            <div
                className="w-full"
                style={{
                    backgroundColor: backgroundColor,
                    color: textColor,
                    fontFamily: getFontFamilyStyle(fontFamily)
                }}
            >
                {templateType === 'inline-button' ? (
                    /* Inline Button Template - Book Now Button */
                    <>
                        <div className="min-h-[400px] flex flex-col lg:flex-row">
                            {/* Mobile: Business Profile comes first (if enabled) */}
                            {showBusinessProfile && (
                                <div className="lg:hidden order-1 mb-8">
                                    <BusinessProfileSection />
                                </div>
                            )}

                            {/* Button Section */}
                            <div className={`flex-1 lg:order-1 order-2 pt-0 px-4 pb-4 lg:px-10 lg:pb-10 lg:pt-6 flex items-center justify-center ${showBusinessProfile ? 'lg:pr-8' : 'lg:pr-10'
                                }`}>
                                <div className="max-w-md w-full text-center">
                                    {/* Header */}
                                    <div className="mb-8">
                                        <h1 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: textColor }}>{widgetTitle}</h1>
                                        <p className="text-lg opacity-75" style={{ color: textColor }}>Click the button below to book your appointment</p>
                                    </div>

                                    {/* Book Now Button */}
                                    <button
                                        onClick={handleOpenModal}
                                        className="w-full py-4 px-8 rounded-lg text-white font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                                        style={{ backgroundColor: accentColor }}
                                    >
                                        Book Now
                                    </button>
                                </div>
                            </div>

                            {/* Desktop: Business Profile (if enabled) */}
                            {showBusinessProfile && (
                                <div className="hidden lg:block lg:order-2">
                                    <BusinessProfileSection />
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    /* Inline Form Template - Full Booking Interface */
                    <div className="flex flex-col lg:flex-row min-h-[700px]">
                        {/* Mobile: Business Profile comes first (if enabled) */}
                        {showBusinessProfile && (
                            <div className="lg:hidden order-1 mb-8">
                                <BusinessProfileSection />
                            </div>
                        )}

                        {/* Left Side - Booking Section */}
                        <div className={`flex-1 lg:order-1 order-2 pt-0 px-4 pb-4 lg:px-10 lg:pb-10 lg:pt-6 ${showBusinessProfile ? 'lg:pr-8' : 'lg:pr-10'
                            }`}>
                            <div className="w-full max-w-none">
                                {/* Header */}
                                <div className="mb-6">
                                    <h1 className="text-2xl font-bold mb-4" style={{ color: textColor }}>{widgetTitle}</h1>
                                    {/* Top separator line */}
                                    <div className="border-t mb-6" style={{ borderColor: textColor, opacity: 0.2 }}></div>
                                </div>

                                <BookingFormContent />
                            </div>
                        </div>

                        {/* Desktop: Business Profile (if enabled) */}
                        {showBusinessProfile && (
                            <div className="hidden lg:block lg:order-2">
                                <BusinessProfileSection />
                            </div>
                        )}
                    </div>
                )}

                {/* Modal for Inline Button Template */}
                {showModal && templateType === 'inline-button' && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                            {/* Background overlay */}
                            <div
                                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                                onClick={handleCloseModal}
                            ></div>

                            {/* Modal panel */}
                            <div className="relative inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
                                {/* Modal header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-xl font-semibold text-gray-900">{widgetTitle}</h2>
                                    <button
                                        onClick={handleCloseModal}
                                        className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                    >
                                        <Icon name="close" size={24} />
                                    </button>
                                </div>

                                {/* Modal body */}
                                <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
                                    <BookingFormContent />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    });

    AppointmentWidgetPreview.displayName = 'AppointmentWidgetPreview';

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleBack}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <Icon name="back" size={24} />
                        </button>
                        <div>
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center">
                                    <img
                                        src="/calendar_icon.svg"
                                        alt="Calendar"
                                        className="w-5 h-5 text-purple-500"
                                        style={{ filter: 'invert(52%) sepia(83%) saturate(2166%) hue-rotate(248deg) brightness(91%) contrast(95%)' }}
                                    />
                                </div>
                                <h1 className="text-xl font-semibold text-gray-900 truncate max-w-md">
                                    {widgetConfig.name || 'Appointment Booking Widget'}
                                </h1>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500 ml-11">
                                <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                                <span>Appointment Widget</span>
                            </div>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                            className="sm:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
                        >
                            <Icon name="menu" size={24} />
                        </button>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onCreateWidget}
                            disabled={isLoading}
                            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 shadow-sm hover:shadow-md"
                        >
                            {isLoading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            )}
                            {!isLoading && (
                                <img
                                    src="/calendar_icon.svg"
                                    alt="Calendar"
                                    className="w-4 h-4"
                                    style={{ filter: 'brightness(0) invert(1)' }}
                                />
                            )}
                            <span>{isLoading ? 'Creating...' : 'Create Widget'}</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors hidden sm:block"
                        >
                            <Icon name="close" size={24} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile sidebar overlay */}
            {showMobileSidebar && (
                <div className="fixed inset-0 z-50 sm:hidden">
                    <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowMobileSidebar(false)}></div>
                    <div className="relative flex flex-col w-full max-w-xs bg-gray-800 text-white shadow-xl">
                        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
                            <h2 className="text-lg font-semibold">Configuration</h2>
                            <button
                                onClick={() => setShowMobileSidebar(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <Icon name="close" size={24} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {/* Mobile tab navigation */}
                            <div className="space-y-1 mb-6">
                                {tabs.map((tab) => {
                                    const validationStatus = getTabValidationStatus(tab.id);
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => {
                                                setActiveTab(tab.id);
                                                setShowMobileSidebar(false);
                                            }}
                                            className={`w-full flex items-center px-3 py-3 rounded-lg transition-colors relative ${activeTab === tab.id
                                                ? 'bg-gray-700 text-white border-l-4 border-purple-500'
                                                : 'hover:bg-gray-700 text-gray-300'
                                                }`}
                                        >
                                            <div className="flex items-center space-x-3 min-w-0">
                                                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                                                    <Icon name={tab.icon} size={18} />
                                                    {validationStatus.needsAttention && (
                                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-gray-800 flex items-center justify-center">
                                                            <div className="w-1 h-1 bg-white rounded-full"></div>
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="font-medium text-sm truncate">{tab.name}</span>
                                                {validationStatus.needsAttention && (
                                                    <div className="ml-auto">
                                                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex h-[calc(100vh-73px)]">
                {/* Left Sidebar - Icon Navigation - Hidden on mobile */}
                <div className="hidden sm:block w-24 bg-gray-800 text-white border-r border-gray-700">
                    <div className="flex flex-col items-center py-6 space-y-2">
                        {tabs.map((tab) => {
                            const validationStatus = getTabValidationStatus(tab.id);
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-20 h-16 flex flex-col items-center justify-center transition-all duration-200 rounded-xl relative ${activeTab === tab.id
                                        ? 'bg-gray-700 text-white shadow-lg'
                                        : 'hover:bg-gray-700 text-gray-400'
                                        }`}
                                    title={validationStatus.needsAttention ? `${tab.name} - ${validationStatus.reason}` : tab.name}
                                >
                                    <div className="flex flex-col items-center space-y-1 min-w-0 w-full">
                                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
                                            <Icon name={tab.icon} size={18} />
                                            {validationStatus.needsAttention && (
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-gray-800 flex items-center justify-center">
                                                    <div className="w-1 h-1 bg-white rounded-full"></div>
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-xs font-medium leading-tight text-center px-1 truncate w-full block">
                                            {tab.name.split(' ').slice(0, 1).join(' ')}
                                        </span>
                                    </div>
                                    {activeTab === tab.id && (
                                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-12 bg-purple-500 rounded-r shadow-lg"></div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right Sidebar - Content Panel - Responsive */}
                <div className="hidden sm:flex sm:w-80 bg-gray-900 text-white flex-col">
                    <div className="flex-1 overflow-y-auto">
                        {/* Validation Alert */}
                        {showValidationAlert && (
                            <div className="m-4 mb-0 p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
                                <div className="flex items-start space-x-3">
                                    <div className="text-red-400 mt-0.5">
                                        <Icon name="info" size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-medium text-red-300 mb-1">Required Information Missing</h4>
                                        <p className="text-xs text-red-200">{validationMessage}</p>
                                        <button
                                            onClick={() => setShowValidationAlert(false)}
                                            className="text-xs text-red-300 hover:text-red-200 mt-2 underline"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Template Tab */}
                        {activeTab === 'template' && (
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-4">Template Options</h3>
                                <p className="text-sm text-gray-400 mb-6">
                                    Choose how you want to display the booking widget
                                </p>

                                <div className="space-y-4">
                                    {templateOptions.map((template) => (
                                        <div
                                            key={template.id}
                                            onClick={() => updateConfig({
                                                appointmentConfig: {
                                                    ...widgetConfig.appointmentConfig,
                                                    templateType: template.id
                                                }
                                            })}
                                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${(widgetConfig.appointmentConfig?.templateType || 'inline-form') === template.id
                                                ? ''
                                                : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800'
                                                }`}
                                            style={(widgetConfig.appointmentConfig?.templateType || 'inline-form') === template.id ? {
                                                borderColor: accentColor,
                                                backgroundColor: `${accentColor}1A` // 10% opacity
                                            } : {}}
                                        >
                                            <div className="flex items-start space-x-3">
                                                <div
                                                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${(widgetConfig.appointmentConfig?.templateType || 'inline-form') === template.id
                                                        ? 'text-white'
                                                        : 'bg-gray-700 text-gray-300'
                                                        }`}
                                                    style={(widgetConfig.appointmentConfig?.templateType || 'inline-form') === template.id ? {
                                                        backgroundColor: accentColor
                                                    } : {}}
                                                >
                                                    <Icon name={template.icon} size={18} />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-sm">{template.name}</h4>
                                                    <p className="text-xs text-gray-400 mt-1">{template.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Services Tab */}
                        {activeTab === 'services' && (
                            <AppointmentServices
                                config={widgetConfig.appointmentConfig || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                                onShowImageCropper={(config) => {
                                    setCropperConfig(config);
                                    setShowImageCropper(true);
                                }}
                            />
                        )}

                        {/* Hours Tab */}
                        {activeTab === 'hours' && (
                            <AppointmentHours
                                config={widgetConfig.appointmentConfig || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                            />
                        )}

                        {/* Business Profile Tab */}
                        {activeTab === 'profile' && (
                            <AppointmentBusinessProfile
                                config={widgetConfig.appointmentConfig || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                                onShowImageCropper={(config) => {
                                    setCropperConfig(config);
                                    setShowImageCropper(true);
                                }}
                            />
                        )}

                        {/* Design Tab */}
                        {activeTab === 'design' && (
                            <AppointmentDesign
                                config={widgetConfig.appointmentConfig || {}}
                                style={widgetConfig.style || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                                onStyleChange={(updates) => updateConfig({
                                    style: {
                                        ...widgetConfig.style,
                                        ...updates
                                    }
                                })}
                            />
                        )}

                        {/* Notifications Tab */}
                        {activeTab === 'notifications' && (
                            <AppointmentNotifications
                                config={widgetConfig.appointmentConfig || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                            />
                        )}

                        {/* Integrations Tab */}
                        {activeTab === 'integrations' && (
                            <AppointmentIntegrations
                                config={widgetConfig.appointmentConfig || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                            />
                        )}

                        {/* Settings Tab */}
                        {activeTab === 'settings' && (
                            <AppointmentSettings
                                config={widgetConfig.appointmentConfig || {}}
                                onConfigChange={(updates) => updateConfig({
                                    appointmentConfig: {
                                        ...widgetConfig.appointmentConfig,
                                        ...updates
                                    }
                                })}
                            />
                        )}
                    </div>
                </div>

                {/* Main Content - Widget Preview */}
                <div className="flex-1 bg-white p-4 sm:p-6 overflow-y-auto overflow-x-hidden">
                    <div className="w-full max-w-none mx-auto">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Widget Preview</h2>
                            <p className="text-gray-600">See how your appointment booking widget will appear on your website</p>
                        </div>

                        {/* Widget Preview */}
                        <div className="w-full overflow-hidden">
                            <AppointmentWidgetPreview />
                        </div>

                        {/* Widget Information */}
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                                <div className="text-blue-500">
                                    <Icon name="info" className="text-blue-500 mt-0.5" size={20} />
                                </div>
                                <div>
                                    <h3 className="font-medium text-blue-800 text-sm">Ready to Accept Bookings</h3>
                                    <p className="text-blue-700 text-sm mt-1">
                                        Your appointment booking widget is configured and ready to deploy. Customers will be able to book appointments directly through your website.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Cropper Modal */}
            <ImageCropper
                isOpen={showImageCropper}
                onClose={() => setShowImageCropper(false)}
                onImageUploaded={(imageUrl) => {
                    console.log('🖼️  Service image uploaded:', imageUrl, 'for service:', cropperConfig.serviceId);
                    if (cropperConfig.type === 'service') {
                        // Handle service image upload
                        const services = widgetConfig.appointmentConfig?.services || [];
                        const updatedServices = services.map(service =>
                            service.id === cropperConfig.serviceId
                                ? { ...service, image: imageUrl, imageUrl: '' } // Clear imageUrl when uploading
                                : service
                        );
                        console.log('🔄 Updated services:', updatedServices);
                        updateConfig({
                            appointmentConfig: {
                                ...widgetConfig.appointmentConfig,
                                services: updatedServices
                            }
                        });
                    }
                    // Note: Cover and logo uploads are now handled directly in AppointmentBusinessProfile component
                }}
                aspectRatio={cropperConfig.aspectRatio || 1}
                imageType="service"
                title="Upload Service Image"
                description="Upload and crop your service image. The image will be optimized and stored securely."
            />
        </div>
    );
});

// Add display name for debugging
AppointmentWidgetConfig.displayName = 'AppointmentWidgetConfig';

export default AppointmentWidgetConfig;
